import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { useParams, useHistory } from 'react-router'
import { GrPrevious, GrNext } from 'react-icons/gr'
import { IoInformationCircle } from 'react-icons/io5'
import { BsStars } from 'react-icons/bs'
import { FaShapes } from 'react-icons/fa6'
import { RxReset } from 'react-icons/rx'
import {
    MdOutlineArrowBack,
    MdFullscreen,
} from 'react-icons/md'

import ContentEngine from '../ContentEngine/ContentEngine'
import MakeContext from './MakeContext'
import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import Sizes from '../../helpers/Sizes'
import './Make.css'

import PlaydynamexNew from '../PlaydynamexNew/PlaydynamexNew'
import IntegrateNew from '../IntegrateNew/IntegrateNew'
import AssemblyTwo from '../AssemblyTwo/index'
import LogicTwo from '../logicTwo/index'
import SimulateNew from '../SimulateNew/SimulateNew'
import PlayScreen from '../PlayScreen/PlayScreen'

const BACKEND_URL = process.env.REACT_APP_API_BASE

function SubprogramRunner({ assemblyTwo, preview = false }) {
    const { subId } = useParams()
    const history = useHistory()
    // Where to send the user when the subprogram finishes / they exit.
    // In admin preview mode we return to the admin detail page instead of
    // the learner's /make dashboard.
    const exitTarget = preview ? `/makeAdmin/${subId}` : '/make'
    const [, forceUpdate] = useState(0)
    const [ready, setReady] = useState(false)

    const [showCompletion, setShowCompletion] = useState(false)
    const [completedTitle, setCompletedTitle] = useState('')
    const timeoutRef = useRef(null)
    // Tracks the in-flight completion sync so navigation waits for it
    const syncRef = useRef(null)

    // --- Shell: persistent header + footer state ---
    const [shellHeaderProps, setShellHeaderProps] = useState(null)
    const [shellFooterProps, setShellFooterProps] = useState(null)
    const [shellToolbarProps, setShellToolbarProps] = useState(null)

    // --- Shell: persistent voiceover audio ---
    // A single <audio> element that lives in the shell (never remounts during
    // screen transitions). Screens play step audio *through* this element via
    // context. Because it is unlocked once by a real user gesture, later
    // programmatic playback (e.g. after a cross-screen jump that remounts the
    // target screen and fetches its data async) is no longer blocked by the
    // browser autoplay policy — which is why audio failed on timeline/next nav.
    const audioRef = useRef(null)
    const audioUnlockedRef = useRef(false)

    useEffect(() => {
        // Unlock the shared audio element on the first user interaction so it
        // becomes "user-activated" for the rest of the session.
        const unlock = () => {
            const a = audioRef.current
            if (!a || audioUnlockedRef.current) return
            audioUnlockedRef.current = true
            a.muted = true
            // A zero-length silent WAV — just enough to activate the element.
            a.src =
                'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA='
            const p = a.play()
            const done = () => {
                a.pause()
                try {
                    a.currentTime = 0
                } catch (e) {}
                a.muted = false
                a.removeAttribute('src')
            }
            if (p && p.then) p.then(done).catch(() => (a.muted = false))
            else done()
        }
        window.addEventListener('pointerdown', unlock)
        window.addEventListener('keydown', unlock)
        return () => {
            window.removeEventListener('pointerdown', unlock)
            window.removeEventListener('keydown', unlock)
        }
    }, [])

    // Stable setters so screens can call them without triggering excess re-renders
    const setHeaderProps = useCallback(
        (props) => setShellHeaderProps(props),
        []
    )
    const setFooterProps = useCallback(
        (props) => setShellFooterProps(props),
        []
    )
    const setToolbarProps = useCallback(
        (props) => setShellToolbarProps(props),
        []
    )

    // Play/stop step audio through the persistent shell element.
    const playAudio = useCallback((url) => {
        const a = audioRef.current
        if (!a || !url) return
        a.muted = false
        a.src = url
        const p = a.play()
        if (p && p.catch) p.catch((e) => console.log('Playback failed:', e))
    }, [])

    const stopAudio = useCallback(() => {
        const a = audioRef.current
        if (!a) return
        a.pause()
        try {
            a.currentTime = 0
        } catch (e) {}
    }, [])

    // Memoised context value – the object reference stays stable so context
    // consumers (screen components) don't re-render just from a footer update
    const tokiContextValue = useMemo(
        () => ({
            isMake: true,
            setHeaderProps,
            setFooterProps,
            setToolbarProps,
            playAudio,
            stopAudio,
        }),
        [setHeaderProps, setFooterProps, setToolbarProps, playAudio, stopAudio]
    )

    // --- NEW: Track if the screen is in portrait mode ---
    // const [isPortrait, setIsPortrait] = useState(
    //     window.innerHeight * 1.5 >= window.innerWidth
    // )

    // --- NEW: Check User Agent for mobile and track portrait mode ---
    const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        )

    const [isMobilePortrait, setIsMobilePortrait] = useState(
        isMobileDevice && window.innerHeight * 1.4 >= window.innerWidth
    )

    useEffect(() => {
        // --- NEW: Resize listener to check aspect ratio dynamically ---
        const handleResize = () => {
            setIsMobilePortrait(
                isMobileDevice && window.innerHeight * 1.4 >= window.innerWidth
            )
        }
        window.addEventListener('resize', handleResize)

        const unsub = ContentEngine.subscribe(() => {
            if (
                !preview &&
                typeof ContentEngine.screenIndex === 'number' &&
                !isNaN(ContentEngine.screenIndex)
            ) {
                const token = localStorage.getItem('userJWT')
                if (token) {
                    fetch(`${BACKEND_URL}/api/progress/sync`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            subprogramId: subId,
                            screenIndex: ContentEngine.screenIndex,
                        }),
                    }).catch((err) =>
                        console.error('Failed to sync screen index', err)
                    )
                }
            }
            forceUpdate((x) => x + 1)
        })

        ContentEngine.onSubprogramComplete = () => {
            const token = localStorage.getItem('userJWT')

            // Store the sync promise so navigating can wait for it to settle.
            // Skip the DB write entirely in preview mode — admins shouldn't
            // mutate learner progress just by previewing a draft.
            if (token && !preview) {
                syncRef.current = fetch(`${BACKEND_URL}/api/progress/sync`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        subprogramId: subId,
                        isCompleted: true,
                    }),
                }).catch((err) =>
                    console.error('Failed to sync completion', err)
                )
            } else {
                syncRef.current = Promise.resolve()
            }

            const currentSubprogram = ContentEngine.getCurrentSubprogram()
            setCompletedTitle(currentSubprogram?.title || 'Project')
            setShowCompletion(true)

            // Wait for DB write to settle before navigating so that
            // GET /api/progress/all in Make sees the new record
            timeoutRef.current = setTimeout(() => {
                ;(syncRef.current || Promise.resolve()).then(() => {
                    history.push(exitTarget, { justCompleted: subId })
                })
            }, 3000)
        }

        // 3. FETCH PROGRESS BEFORE LOADING THE PROGRAM
        const initializeProgram = async () => {
            // Route ContentEngine to the staging (unpublished) endpoints when
            // previewing, and back to live otherwise. Set before loadProgram.
            ContentEngine.staging = preview

            let savedIndex = 0
            const token = localStorage.getItem('userJWT')

            // Fetch progress from DB (skip in preview — always start at screen 0)
            if (token && !preview) {
                try {
                    const res = await fetch(
                        `${BACKEND_URL}/api/progress/${subId}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                            credentials: 'include',
                        }
                    )
                    if (res.ok) {
                        const data = await res.json()
                        if (
                            data.progress &&
                            data.progress.screenIndex !== undefined
                        ) {
                            savedIndex = data.progress.screenIndex
                        }
                    }
                } catch (e) {
                    console.error('Error fetching progress from DB', e)
                }
            }

            // Load Engine — resolve which board/program this subprogram
            // belongs to (Toki, Poki, …) from the device the admin/learner
            // selected. Hardcoding 'toki' meant Poki subprograms were never
            // found, so the Start Preview button silently bounced back.
            const programId = (
                sessionStorage.getItem('allowedDevice') || 'toki'
            ).toLowerCase()
            await ContentEngine.loadProgram(programId)

            const subIdx = ContentEngine.program?.subprograms?.findIndex(
                (s) => s.id === subId
            )

            if (subIdx !== undefined && subIdx >= 0) {
                ContentEngine.subIndex = subIdx

                const currentSub = ContentEngine.program.subprograms[subIdx]
                const maxIndex = currentSub?.screens
                    ? currentSub.screens.length - 1
                    : 0

                // Validate index bounds
                if (
                    isNaN(savedIndex) ||
                    savedIndex < 0 ||
                    savedIndex > maxIndex
                ) {
                    savedIndex = 0
                }

                ContentEngine.screenIndex = savedIndex

                ContentEngine.loadCurrentScreenData().then(() => {
                    ContentEngine.notify()
                    setReady(true)
                })
            } else {
                console.error('Subprogram not found:', subId)
                history.push(preview ? exitTarget : '/kitSelection')
            }
        }

        initializeProgram()

        return () => {
            unsub()
            ContentEngine.onSubprogramComplete = null
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
            window.removeEventListener('resize', handleResize)
        }
    }, [subId, history, preview, exitTarget])

    const handleOverlayClick = () => {
        if (timeoutRef.current)
            clearTimeout(timeoutRef.current)
            // Await the sync before navigating (handles immediate overlay clicks)
        ;(syncRef.current || Promise.resolve()).then(() => {
            history.push(exitTarget, { justCompleted: subId })
        })
    }

    const screen = ContentEngine.getCurrentScreen()
    const data = ContentEngine.currentScreenData
    const screenKey = `${ContentEngine.subIndex}-${ContentEngine.screenIndex}`
    const slideClass = 'toki-dissolve-in'
    const projectId =
        screen?.projectId || ContentEngine.getCurrentSubprogram()?.id || null
    const screenId = screen?.id || null

    if (projectId) {
        sessionStorage.setItem('apiProjectId', projectId)
    }
    // Drive the assembly component catalog off the active board instead of
    // always forcing Toki, so Poki (and future boards) surface their own parts.
    const allowedDevice = sessionStorage.getItem('allowedDevice') || 'Toki'
    const connectedDevice =
        allowedDevice.charAt(0).toUpperCase() +
        allowedDevice.slice(1).toLowerCase()
    sessionStorage.setItem('connectedDevice', connectedDevice)

    // Preserve the existing deviceVersion instead of resetting to '1'
    if (!sessionStorage.getItem('deviceVersion')) {
        sessionStorage.setItem('deviceVersion', '1')
    }

    let assemblyScreenId = null
    if (screen?.type === 'hex') {
        const sub = ContentEngine.getCurrentSubprogram()
        if (sub?.screens) {
            for (let i = ContentEngine.screenIndex - 1; i >= 0; i--) {
                if (sub.screens[i].type === 'assembly') {
                    assemblyScreenId = sub.screens[i].id
                    break
                }
            }
        }
        if (assemblyScreenId) {
            sessionStorage.setItem('assemblyScreenId', assemblyScreenId)
        }
    }

    const pendingStep = ContentEngine._pendingStep
    if (pendingStep != null) {
        setTimeout(() => {
            ContentEngine._pendingStep = null
        }, 0)
    }

    if (!ready || !screen || !data) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    fontSize: '1.2rem',
                    color: '#888',
                }}
            >
                {/* Loading... */}
            </div>
        )
    }

    return (
        <div className="make-page toki-shell-active">
            {/* ── Persistent voiceover audio (never unmounts across screens) ── */}
            <audio ref={audioRef} style={{ display: 'none' }} />

            {/* ── Admin preview banner / exit button ── */}
            {preview && !isMobilePortrait && (
                <button
                    onClick={() => history.push(exitTarget)}
                    title="Return to the admin editor"
                    style={{
                        position: 'fixed',
                        top: '102px',
                        right: '52px',
                        zIndex: 10000,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 14px',
                        borderRadius: '999px',
                        border: 'none',
                        background: '#DC3545',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                    }}
                >
                    <MdOutlineArrowBack size={18} /> Exit Preview
                </button>
            )}

            {/* ── Persistent header (never unmounts during screen transitions) ── */}
            {shellHeaderProps && !isMobilePortrait && (
                <div className="toki-shell-header">
                    <MakeHeader showTimeline {...shellHeaderProps} />
                </div>
            )}

            {/* ── Sliding content area ── */}
            <div className="make-content">
                <div
                    key={screenKey}
                    className={slideClass}
                    style={{ width: '100%', height: '100%' }}
                >
                    <MakeContext.Provider value={tokiContextValue}>
                        {renderScreen(
                            screen,
                            data,
                            screenKey,
                            assemblyTwo,
                            projectId,
                            screenId,
                            assemblyScreenId,
                            pendingStep
                        )}
                    </MakeContext.Provider>
                </div>
            </div>

            {/* ── Persistent footer (never unmounts during screen transitions) ── */}
            {shellFooterProps && !isMobilePortrait && (
                <footer className="toki-shell-footer">
                    <div className="step-counter">
                        {shellFooterProps.stepCounter}
                    </div>
                    <div className="step-title">
                        {shellFooterProps.stepTitle}
                    </div>
                    <div className="nav-buttons">
                        <button
                            className="nav-btn"
                            onClick={shellFooterProps.onPrev}
                            disabled={!!shellFooterProps.prevDisabled}
                        >
                            <GrPrevious />
                            <span className="footer-btn-text">Previous</span>
                        </button>
                        <div className="next-btn-wrapper">
                            {shellFooterProps.showNextTooltip && (
                                <div className="next-btn-tooltip">
                                    <IoInformationCircle />{' '}
                                    {shellFooterProps.nextTooltipText ||
                                        'Complete the action to proceed'}
                                </div>
                            )}
                            <button
                                className={`nav-btn ${shellFooterProps.nextClassName || ''}`}
                                onClick={shellFooterProps.onNext}
                                disabled={!!shellFooterProps.nextDisabled}
                            >
                                <span className="footer-btn-text">
                                    {shellFooterProps.nextLabel || 'Next'}
                                </span>
                                <GrNext />
                            </button>
                        </div>
                    </div>
                </footer>
            )}

            {/* ── Persistent toolbar (never unmounts during screen transitions) ── */}
            {shellToolbarProps && !isMobilePortrait && (
                <div className="toki-shell-toolbar">
                    {shellToolbarProps.onMaterialsToggle && (
                        <button
                            className={`toolbar-button ${shellToolbarProps.showMaterials ? 'active' : ''}`}
                            title="Materials"
                            onClick={shellToolbarProps.onMaterialsToggle}
                        >
                            <FaShapes />
                        </button>
                    )}
                    <button
                        className={`toolbar-button ${shellToolbarProps.aiVoiceover ? 'active' : ''}`}
                        title="AI Voiceover"
                        onClick={shellToolbarProps.onAiToggle}
                    >
                        <BsStars />
                    </button>
                    <button
                        className="toolbar-button"
                        title="Fullscreen"
                        onClick={shellToolbarProps.onFullscreen}
                    >
                        <MdFullscreen />
                    </button>
                    <button
                        className="toolbar-button"
                        title="Reset"
                        onClick={shellToolbarProps.onReset}
                    >
                        <RxReset />
                    </button>
                </div>
            )}

            {/* --- NEW: The Portrait/Mobile Restriction Overlay --- */}
            {isMobilePortrait && (
                <div className="portrait-overlay">
                    <div className="completion-content">
                        <div className="portrait-icon">
                            <svg viewBox="0 0 24 24" width="80" height="80">
                                <rect
                                    x="2"
                                    y="4"
                                    width="20"
                                    height="12"
                                    rx="2"
                                    fill="none"
                                    stroke="#f8b26a"
                                    strokeWidth="2"
                                />
                                <path
                                    d="M6 20h12M12 16v4"
                                    stroke="#f8b26a"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <h1 className="portrait-title">Switch Device</h1>
                        <p className="portrait-subtitle">
                            Please access this content on a{' '}
                            <strong>PC, Laptop, or a Landscape Tablet</strong>.
                            Portrait ratios and mobile devices are not
                            supported.
                        </p>
                    </div>
                </div>
            )}

            {/* --- The Completion Overlay --- */}
            {showCompletion && !isMobilePortrait && (
                <div
                    className="completion-overlay"
                    onClick={handleOverlayClick}
                >
                    <div className="completion-content">
                        <div className="completion-icon">
                            <svg viewBox="0 0 24 24" width="80" height="80">
                                <circle cx="12" cy="12" r="12" fill="#abbd81" />
                                <path
                                    d="M7 12l3 3 7-7"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <h1 className="completion-title">Awesome Job!</h1>
                        <h2 className="completion-subtitle">
                            You completed <strong>{completedTitle}</strong>
                        </h2>
                        <p className="completion-hint">
                            Click anywhere to continue
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

function renderScreen(
    screen,
    data,
    screenKey,
    assemblyTwo,
    projectId,
    screenId,
    assemblyScreenId,
    initialStep
) {
    switch (screen.type) {
        case 'playdynamex':
            return (
                <PlaydynamexNew
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    initialStep={initialStep}
                />
            )
        case 'integrate':
            return (
                <IntegrateNew
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    initialStep={initialStep}
                />
            )
        case 'assembly':
            const height = assemblyTwo ? assemblyTwo.height : '100%'
            return (
                <AssemblyTwo
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    height={height}
                    width={Sizes.width}
                    initialStep={initialStep}
                />
            )
        case 'hex':
            return (
                <LogicTwo
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    assemblyScreenId={assemblyScreenId}
                    initialStep={initialStep}
                />
            )
        case 'simulate':
            return (
                <SimulateNew
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    initialStep={initialStep}
                />
            )
        case 'play':
            return (
                <PlayScreen
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    initialStep={initialStep}
                />
            )
        default:
            return <div>Unknown screen type: {screen.type}</div>
    }
}

const mapStateToProps = (state) => ({
    assemblyTwo: state.assemblyTwo,
})

export default connect(mapStateToProps)(SubprogramRunner)
